import {Avatar, Button, Dropdown, Grid, Layout, Segmented, Typography} from "antd";
import {Link, useLocation} from "react-router";
import {logout, setCollapseSidebar, setTheme, useAppDispatch, useAppSelector} from "@/store";
import {
    AppstoreOutlined,
    DownOutlined,
    MenuFoldOutlined,
    MenuOutlined,
    MenuUnfoldOutlined,
    MoonOutlined,
    PlusOutlined,
    SettingOutlined,
    DesktopOutlined,
    SunOutlined
} from "@ant-design/icons";
import LogoComponent from "@/assets/logoComponent.tsx";
import {clearAuthCookies, getInitials} from "@/utils";
import {supabase} from "@/utils/supabase.ts";

const {Header} = Layout;
const {Title, Text} = Typography;
const {useBreakpoint} = Grid;

const routeMeta: Record<string, {label: string; eyebrow: string}> = {
    "/": {label: "Dashboard", eyebrow: "Overview"},
    "/listings": {label: "Listings", eyebrow: "Inventory"},
    "/auctions": {label: "Auctions", eyebrow: "Marketplace"},
    "/messages": {label: "Messages", eyebrow: "Inbox"},
    "/settings": {label: "Settings", eyebrow: "Workspace"},
    "/create": {label: "Create Vehicle", eyebrow: "Publishing"}
};

function getRouteMeta(pathname: string) {
    if (pathname.startsWith("/listing/")) {
        return {label: "Listing", eyebrow: "Inventory"};
    }
    if (pathname.startsWith("/auction/")) {
        return {label: "Auction", eyebrow: "Marketplace"};
    }
    if (pathname.startsWith("/vehicle/")) {
        return {label: "Vehicle", eyebrow: "Inventory"};
    }

    return routeMeta[pathname] ?? {label: "Wheela", eyebrow: "Dealer Workspace"};
}

export default function Navbar() {
    const {user, dealerUser} = useAppSelector((state) => state.authentication);
    const {theme, collapseSidebar} = useAppSelector((state) => state.main);
    const dispatch = useAppDispatch();
    const location = useLocation();
    const screens = useBreakpoint();
    const isMobile = !screens.lg;

    const displayName = [dealerUser?.firstName, dealerUser?.lastName].filter(Boolean).join(" ").trim() || user?.name || "Dealer";
    const avatar = dealerUser?.profile ?? user?.profile;
    const initials = getInitials(displayName);
    const route = getRouteMeta(location.pathname);

    const handleLogout = async () => {
        clearAuthCookies();
        await supabase.auth.signOut();
        dispatch(logout());
    };

    return (
        <Header className=" sticky top-0 z-30 h-auto! bg-white/95! shadow-accent/20  py-1! px-3! shadow-md! backdrop-blur-xl! sm:px-4! md:px-6!  dark:bg-black/75!">
            <div className="flex items-center justify-between gap-3">
                <div className="min-w-0 flex items-center gap-3">
                    <Link to="/" className="flex min-w-0 items-center gap-3">
                        <Avatar src={<LogoComponent className={'fill-primary dark:fill-accent'}/>}/>

                        <div className="min-w-0">
                            <Text className="block text-[11px]!  leading-none! uppercase tracking-[0.22em] text-white/45">
                                {route.eyebrow}
                            </Text>
                            <Title level={isMobile? 5 : 4} className="mb-0! mt-1! leading-none! font-logo truncate text-white">
                                {isMobile ? route.label : "SERID Wheels"}
                            </Title>
                        </div>
                    </Link>

                    {!isMobile ? (
                        <Button
                            type="text"
                            className="border border-white/10 bg-white/5 text-white hover:border-white/20 hover:bg-white/10!"
                            onClick={() => dispatch(setCollapseSidebar(!collapseSidebar))}
                            icon={collapseSidebar ? <MenuUnfoldOutlined/> : <MenuFoldOutlined/>}
                        />
                    ) : null}
                </div>

                <div className="flex shrink-0 items-center gap-2 sm:gap-3">
                    {!isMobile ? (
                        <>
                            <Link to="/create">
                                <Button type="primary" icon={<PlusOutlined/>}>
                                    Create
                                </Button>
                            </Link>

                            <Segmented
                                onChange={(value) => dispatch(setTheme(value as "light" | "system" | "dark"))}
                                shape="round"
                                options={[
                                    {value: "light", icon: <SunOutlined/>},
                                    {value: "system", icon: <DesktopOutlined/>},
                                    {value: "dark", icon: <MoonOutlined/>}
                                ]}
                                value={theme}
                            />
                        </>
                    ) : (
                        <Link to="/create">
                            <Button type="text" className="text-white" icon={<PlusOutlined/>}/>
                        </Link>
                    )}

                    {user && !isMobile ? (
                        <Dropdown
                            placement="bottomRight"
                            arrow
                            menu={{
                                items: [
                                    {key: "settings", icon: <SettingOutlined/>, label: <Link to="/settings">Settings</Link>},
                                    {key: "listings", icon: <AppstoreOutlined/>, label: <Link to="/listings">Listings</Link>},
                                    {key: "logout", label: "Logout", onClick: () => void handleLogout()}
                                ]
                            }}
                        >
                            <button
                                type="button"
                                className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-2.5 py-2 text-left text-white transition hover:border-white/20 hover:bg-white/10"
                            >
                                <Avatar shape="circle" size="large" src={avatar}>
                                    {!avatar && initials}
                                </Avatar>
                                <div className="min-w-0">
                                    <Text className="block truncate text-sm! text-white">
                                        {displayName}
                                    </Text>
                                    <Text className="block truncate text-xs! text-white/45">
                                        {dealerUser?.role === "admin" ? "Admin" : "Team Member"}
                                    </Text>
                                </div>
                                <DownOutlined className="text-white/45"/>
                            </button>
                        </Dropdown>
                    ) : (
                        <Button
                            size="large"
                            icon={<MenuOutlined/>}
                            type="text"
                            className="text-white"
                            onClick={() => dispatch(setCollapseSidebar(!collapseSidebar))}
                        />
                    )}
                </div>
            </div>
        </Header>
    );
}
